import React from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import CommonButton from './CommonButton';
import CommonModal from './CommonModal';
import type { ApprovalApiItem } from '../api';
import { formatDateOnly } from '../utils/dateFormat';

export interface TicketSnapshotModalProps {
  open: boolean;
  onClose: () => void;
  approvalId: string;
  ticketId: string;
  claimNumber: string;
  department: string;
  requester: string;
  ticketStatus: string;
  submissionStatus: string;
  snapshot?: ApprovalApiItem['follow_up_snapshot'];
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
};

const Field: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
  <Stack spacing={0.1}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2">{formatValue(value)}</Typography>
  </Stack>
);

// For actual date fields (follow-up/resolution/call dates) — these come through
// as raw ISO strings, and formatValue's generic String(value) would print them
// unformatted, so they need the shared date formatter instead.
const DateField: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
  <Stack spacing={0.1}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2">{formatDateOnly(value)}</Typography>
  </Stack>
);

const SectionGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ columnGap: 3, rowGap: 1.25 }}>
    {children}
  </Stack>
);

// Read-only view of a ticket, sourced from the approval's follow_up_snapshot —
// i.e. exactly the state the ticket was in when it was submitted for approval,
// not necessarily its live current state. Shared by AuditApprovalsPage and
// ApprovalsFlowPage so QC/Lead/Manager can see full ticket context without
// needing separate Ticket Bucket access.
const TicketSnapshotModal: React.FC<TicketSnapshotModalProps> = ({
  open,
  onClose,
  approvalId,
  ticketId,
  claimNumber,
  department,
  requester,
  ticketStatus,
  submissionStatus,
  snapshot,
}) => {
  const basic = snapshot?.basic;
  const financial = snapshot?.financial;
  const callLog = snapshot?.call_log;
  const financialEntries = Object.entries(financial ?? {}).filter(([, value]) => value !== undefined);

  return (
    <CommonModal
      open={open}
      title={`Ticket Details — ${ticketId}`}
      onClose={onClose}
      maxWidth="md"
      actions={<CommonButton label="Close" variant="outlined" onClick={onClose} />}
    >
      <Stack spacing={2}>
        <SectionGrid>
          <Field label="Approval ID" value={approvalId} />
          <Field label="Ticket ID" value={ticketId} />
          <Field label="Claim Number" value={claimNumber} />
          <Field label="Department" value={department} />
          <Field label="Requested By" value={requester} />
          <Field label="Ticket Status" value={ticketStatus} />
          <Field label="Submission Status" value={submissionStatus} />
        </SectionGrid>

        {basic && (
          <>
            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>Follow-Up</Typography>
            <SectionGrid>
              <Field label="Assigned To" value={basic.assigned_to_name} />
              <Field label="Policy / Member ID" value={basic.policy_member_id} />
              <Field label="Provider / Practice" value={basic.provider_practice_name} />
              <DateField label="Follow-up Date" value={basic.follow_up_date} />
              <DateField label="Next Follow-up Date" value={basic.next_follow_up_date} />
              <DateField label="Resolution Date" value={basic.resolution_date} />
            </SectionGrid>
            {basic.description && (
              <Stack spacing={0.1}>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body2">{basic.description}</Typography>
              </Stack>
            )}
            {basic.key_points && (
              <Stack spacing={0.1}>
                <Typography variant="caption" color="text.secondary">Key Points</Typography>
                <Typography variant="body2">{basic.key_points}</Typography>
              </Stack>
            )}
          </>
        )}

        {callLog && (
          <>
            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>Call Log</Typography>
            <SectionGrid>
              <DateField label="Call Date" value={callLog.call_date} />
              <Field label="Call Type" value={callLog.call_type} />
              <Field label="Start Time" value={callLog.call_start_time} />
              <Field label="End Time" value={callLog.call_end_time} />
              <Field label="Duration" value={callLog.call_duration} />
              <Field label="Reference Number" value={callLog.call_reference_number} />
            </SectionGrid>
          </>
        )}

        {financialEntries.length > 0 && (
          <>
            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>Financial</Typography>
            <SectionGrid>
              {financialEntries.map(([key, value]) => (
                <Field
                  key={key}
                  label={key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}
                  value={value}
                />
              ))}
            </SectionGrid>
          </>
        )}

        {!basic && !callLog && financialEntries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No follow-up details were recorded on this ticket at the time it was submitted for approval.
          </Typography>
        )}
      </Stack>
    </CommonModal>
  );
};

export default TicketSnapshotModal;
